import { z } from 'zod';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Transferência entre contas correntes.
 *
 * O par saída/entrada do caixa, num documento só: o dinheiro sai da conta de
 * origem e entra na de destino **pelo mesmo valor**, na mesma transação. É o
 * espelho financeiro da transferência de estoque (`.spec/015`, decisão 30), e a
 * frase que descreve as duas é a mesma — *o valor não muda de tamanho, só de
 * lugar*.
 *
 * ## Por que não é um lançamento manual
 *
 * O lançamento em conta corrente aceita conta contábil de **resultado** apenas
 * — receita no crédito, custo ou despesa no débito (`GRUPOS_POR_TIPO_MOVIMENTO`,
 * em `lancamentos.ts`), e o comentário de lá já reservava este caso: *"ativo e
 * passivo ficam de fora dos dois: movimentação entre contas de balanço
 * (transferência entre bancos) não é lançamento manual de caixa com
 * contrapartida de resultado"*. Transferir não forma resultado nenhum — nem
 * receita nem despesa —, e classificar as duas pernas numa conta de DRE faria a
 * mesma quantia aparecer como receita de um lado e despesa do outro, inflando as
 * duas metades do resultado do exercício por um dinheiro que nunca saiu do grupo.
 *
 * Por isso a transferência escreve **direto no livro-razão**, como quarta origem
 * de `movimentacoes_conta_corrente` — a mesma escolha que a transferência de
 * estoque fez em `movimentacoes_estoque`. Duas consequências, e as duas são o
 * que se queria: as pernas aparecem no extrato, que é onde a conta corrente se
 * lê; e **não há como excluí-las por fora**, porque o livro-razão não tem rota
 * de escrita nenhuma. Quem desfaz as duas é a exclusão deste documento.
 *
 * ## Por que não há empresa no cabeçalho
 *
 * A transferência atravessa empresas do grupo de propósito — é o caso de quem
 * concentra caixa numa conta e distribui para as outras. Uma `empresa_id` no
 * documento teria de valer para as duas pernas, e valeria errado numa delas.
 * **A empresa de cada movimentação é a da conta corrente que ela move**, lida do
 * cadastro no momento da gravação.
 *
 * É a diferença deliberada em relação à transferência de estoque, que exige uma
 * empresa só: lá o material que troca de pessoa jurídica é fato fiscal e pede
 * nota; aqui não há fato fiscal — dinheiro entre contas do mesmo grupo é
 * movimentação de caixa, e o que ela deixa em aberto é um acerto entre as duas
 * empresas, não um tributo.
 */

/* -------------------------------------------------------------------------- */
/* Valor                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Valor transferido: **sempre positivo**, e um só para as duas pernas.
 *
 * Não há "valor de origem" e "valor de destino". Dois campos permitiriam gravar
 * um documento que tira 1.000 de uma conta e põe 900 na outra, e os 100 de
 * diferença não teriam onde aparecer — nem no extrato, nem no balancete. Tarifa
 * de TED é outro fato, com histórico próprio, e se lança como lançamento manual
 * de débito.
 */
const valorDaTransferenciaSchema = dinheiroSchema.refine(
  (v) => Number(v) > 0,
  'O valor da transferência deve ser maior que zero.',
);

/**
 * Origem e destino diferentes.
 *
 * A recusa vale nos três lugares — contrato, service e `CHECK` da tabela — e
 * aqui ela ganha o `path`, que é o que prende a mensagem ao campo certo do
 * formulário. Sem isto o documento admitiria um débito e um crédito na mesma
 * conta: o saldo fecharia — sai mil, entra mil —, e o extrato mostraria duas
 * linhas de um movimento que não aconteceu.
 */
function recusarContaIgual(
  dados: { contaCorrenteOrigemId: string; contaCorrenteDestinoId: string },
  ctx: z.RefinementCtx,
): void {
  if (dados.contaCorrenteOrigemId !== dados.contaCorrenteDestinoId) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['contaCorrenteDestinoId'],
    message: 'A conta de destino precisa ser diferente da de origem.',
  });
}

/* -------------------------------------------------------------------------- */
/* Payload                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * O que o usuário digita.
 *
 * **Quatro campos e uma observação.** O que não está aqui é tão decidido quanto
 * o que está:
 *
 * - **`empresaId`** — não existe. A empresa de cada perna é a da conta corrente
 *   dela, e a transferência entre empresas do grupo é caso legítimo.
 * - **`historicoBancarioId`** — sai do parâmetro da conta
 *   (`historicoBancarioTransferenciaId`, em `conta.ts`). Toda transferência é a
 *   mesma natureza de movimento, e perguntá-la a cada documento só produziria
 *   extratos em que a mesma operação aparece com três nomes diferentes.
 * - **`contaContabilId`** — sai do parâmetro pelo mesmo motivo, e por um a mais:
 *   ela é conta de balanço, e este formulário não tem por que ensinar a
 *   diferença entre balanço e resultado a quem só quer mover dinheiro.
 * - **`tipo`** — não existe. O sentido é o par origem/destino: a origem é sempre
 *   débito e o destino sempre crédito. Um campo de sentido permitiria gravar
 *   "crédito na origem", que é a transferência ao contrário escrita de um jeito
 *   que ninguém lê.
 */
export const criarTransferenciaContaCorrenteSchema = z
  .object({
    /** Dia da transferência. Data de negócio, sem hora: é do dia 10. */
    data: dataDoFatoSchema,
    /** De onde o dinheiro sai. Esta conta é debitada. */
    contaCorrenteOrigemId: uuidSchema,
    /** Para onde o dinheiro vai. Esta conta é creditada, pelo mesmo valor. */
    contaCorrenteDestinoId: uuidSchema,
    /** Forma de pagamento: pix, TED, cheque, dinheiro. */
    historicoFinanceiroId: uuidSchema,
    valor: valorDaTransferenciaSchema,
    /**
     * Opcional, ao contrário da transferência de estoque, onde ela é exigida.
     *
     * Lá a pergunta que se faz meses depois é "por que este material foi parar
     * naquele canteiro", e só o texto responde. Aqui as duas contas e o valor já
     * contam a história inteira, e um campo obrigatório sem nada a dizer produz
     * o "transferência" digitado para o formulário deixar salvar.
     */
    observacao: z.string().trim().max(500).prefault(''),
  })
  .superRefine(recusarContaIgual);

export type CriarTransferenciaContaCorrente = z.infer<typeof criarTransferenciaContaCorrenteSchema>;

export const atualizarTransferenciaContaCorrenteSchema = criarTransferenciaContaCorrenteSchema;
export type AtualizarTransferenciaContaCorrente = z.infer<
  typeof atualizarTransferenciaContaCorrenteSchema
>;

/* -------------------------------------------------------------------------- */
/* Leitura                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A transferência como a API a devolve.
 *
 * Descrição das contas, nome das empresas e descrição do histórico vêm
 * **desnormalizados**: a listagem mostra "Banco do Brasil 1234-5 · Construtora
 * Aurora", não dois uuids, e resolver isso no cliente significaria quatro
 * consultas por linha de tabela.
 *
 * As **empresas das duas pontas** aparecem porque a transferência entre empresas
 * do grupo é o caso que este documento existe para permitir: sem elas, duas
 * contas chamadas "Caixa" em empresas diferentes são indistinguíveis na tela.
 */
export const transferenciaContaCorrenteSchema = z.object({
  id: uuidSchema,
  data: dataSchema,

  contaCorrenteOrigemId: uuidSchema,
  contaCorrenteOrigemDescricao: z.string().nullable(),
  empresaOrigemId: uuidSchema.nullable(),
  empresaOrigemNome: z.string().nullable(),

  contaCorrenteDestinoId: uuidSchema,
  contaCorrenteDestinoDescricao: z.string().nullable(),
  empresaDestinoId: uuidSchema.nullable(),
  empresaDestinoNome: z.string().nullable(),

  historicoFinanceiroId: uuidSchema,
  historicoFinanceiroDescricao: z.string().nullable(),

  /** String decimal, nunca number: ponto flutuante perde centavos em silêncio. */
  valor: dinheiroSchema,
  observacao: z.string(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type TransferenciaContaCorrente = z.infer<typeof transferenciaContaCorrenteSchema>;

export const listaTransferenciasContaCorrenteSchema = respostaListaSchema(
  transferenciaContaCorrenteSchema,
);
export type ListaTransferenciasContaCorrente = z.infer<
  typeof listaTransferenciasContaCorrenteSchema
>;

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: transferência é documento de movimento, não cadastro — ela existe
 * ou não existe.
 *
 * `contaCorrenteId` alcança as **duas pontas**. "O que entrou e saiu desta
 * conta" é a pergunta com que a tela abre, e dois filtros separados obrigariam a
 * fazer a mesma busca duas vezes e somar de cabeça. Quem quiser um lado só usa
 * `contaCorrenteOrigemId` ou `contaCorrenteDestinoId` — é a mesma escolha da
 * listagem de transferências de estoque.
 */
export const listarTransferenciasContaCorrenteQuerySchema = paginacaoSchema.extend({
  /** Qualquer das duas pontas. */
  contaCorrenteId: uuidSchema.optional(),
  contaCorrenteOrigemId: uuidSchema.optional(),
  contaCorrenteDestinoId: uuidSchema.optional(),
  /** A empresa de qualquer das duas contas. */
  empresaId: uuidSchema.optional(),
  historicoFinanceiroId: uuidSchema.optional(),
  /** Início do período, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarTransferenciasContaCorrenteQuery = z.infer<
  typeof listarTransferenciasContaCorrenteQuerySchema
>;

export const COLUNAS_ORDENAVEIS_TRANSFERENCIA_CONTA_CORRENTE = [
  'data',
  'valor',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Regra compartilhada pela tela e pela API                                   */
/* -------------------------------------------------------------------------- */

/**
 * Como uma conta corrente se chama num seletor que mistura empresas.
 *
 * `Banco do Brasil 1234-5 · Construtora Aurora`.
 *
 * Todo grupo tem uma conta "Caixa" e uma "Banco do Brasil" por empresa, e este
 * é o único documento do sistema cujos dois seletores de conta **não** são
 * recortados por uma empresa escolhida antes — é justamente a travessia entre
 * empresas que ele existe para permitir. Sem o nome da empresa no rótulo, as
 * duas pontas mostram a mesma lista de nomes repetidos e a escolha vira sorteio.
 *
 * Fica no contrato, e não na tela, porque a listagem e o formulário precisam do
 * **mesmo** rótulo: um documento gravado se lê na tabela com o nome que se
 * escolheu no seletor, e duas versões da mesma frase divergem na primeira
 * correção.
 */
export function rotuloDaContaCorrente(
  descricao: string | null,
  empresaNome: string | null,
): string {
  const conta = descricao ?? '';
  return empresaNome ? `${conta} · ${empresaNome}` : conta;
}
